import React, { forwardRef, RefObject, useCallback, useImperativeHandle, useRef } from 'react';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';
import { useDebouncedCallback } from 'new/hooks/useDebouncedCallback';
import { CircularProgress, Stack } from '@mui/material';
import { VIRTUALIZED_LIST } from '../../constants';
import s from './VirtualizedLogList.module.scss';

interface VirtualizedLogListProps<T> {
  items: T[];
  renderItem: (item: T, index: number) => JSX.Element;
  loading?: boolean;
  totalCount?: number;
  onEndReached?: () => void;
  onStartReached?: () => void;
  atTopThreshold?: number;
  firstItemIndex?: number;
  initialTopMostItemIndex?: number;
  height?: number | string;
  followOutput?: boolean | 'smooth' | ((isAtBottom: boolean) => boolean | 'smooth');
  components?: {
    Header?: React.ComponentType;
    Footer?: React.ComponentType;
    EmptyPlaceholder?: React.ComponentType;
  };
  parentScrollRef?: RefObject<HTMLElement>;
  onAtBottomStateChange?: (isAtBottom: boolean) => void;
}

export interface VirtualizedLogListHandle {
  scrollToTop: () => void;
  scrollToBottom: () => void;
  scrollToIndex: (options: {
    index: number | 'LAST';
    align?: 'start' | 'center' | 'end';
    behavior?: 'auto' | 'smooth';
  }) => void;
}

function DefaultLoader({ loading, numItems }) {
  if (!loading) return null;
  return (
    <Stack alignItems="center" justifyContent="center" height={numItems && numItems > 0 ? 70 : 200}>
      <CircularProgress />
    </Stack>
  );
}

const DEBOUNCE_DELAY = 1000;

const styles = {
  width: '100%',
  height: 'calc(100vh - 200px)',
  overscrollBehavior: 'contain' as const,
  WebkitOverflowScrolling: 'touch' as const,
};

const containerStyles = {
  position: 'relative' as const,
  width: '100%',
  height: '100%',
};

export const VirtualizedLogList = forwardRef<
  VirtualizedLogListHandle,
  VirtualizedLogListProps<any>
>((props, ref) => {
  const {
    items,
    loading,
    renderItem,
    onEndReached,
    onStartReached,
    atTopThreshold,
    firstItemIndex,
    initialTopMostItemIndex,
    totalCount,
    followOutput,
    components,
    parentScrollRef,
    onAtBottomStateChange,
  } = props;

  const virtuosoRef = useRef<VirtuosoHandle>(null);
  const lastStartReachedTime = useRef(0);

  useImperativeHandle(ref, () => ({
    scrollToTop: () => {
      virtuosoRef.current?.scrollToIndex({
        index: 0,
        align: 'start',
        behavior: 'smooth',
      });
    },
    scrollToBottom: () => {
      virtuosoRef.current?.scrollToIndex({
        index: 'LAST',
        align: 'end',
        behavior: 'smooth',
      });
    },
    scrollToIndex: (options) => {
      virtuosoRef.current?.scrollToIndex(options);
    },
  }));

  const handleStartReached = useCallback(() => {
    if (!onStartReached) return;

    const now = Date.now();
    const shouldSkip = now - lastStartReachedTime.current < DEBOUNCE_DELAY;

    if (shouldSkip) return;

    lastStartReachedTime.current = now;
    onStartReached?.();
  }, [onStartReached]);

  const rowRenderer = useCallback(
    (index: number, item: any) => {
      return renderItem(item, index);
    },
    [renderItem],
  );

  const computeItemKey = useCallback((index: number, item: any) => {
    if (
      item &&
      typeof item === 'object' &&
      item.id &&
      typeof item.id === 'string' &&
      item.id.length > 0
    ) {
      return item.id;
    }

    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();

    return `item-${index}`;
  }, []);

  const endReached = useDebouncedCallback(onEndReached, [onEndReached], 1000, {
    throttle: true,
  });

  return (
    <div style={containerStyles}>
      <Virtuoso
        ref={virtuosoRef}
        style={styles}
        className={s.virtuosoScroller}
        data={items}
        totalCount={totalCount}
        itemContent={rowRenderer}
        computeItemKey={computeItemKey}
        endReached={!!onEndReached ? endReached : undefined}
        startReached={!!onStartReached ? handleStartReached : undefined}
        atTopThreshold={atTopThreshold}
        firstItemIndex={firstItemIndex}
        initialTopMostItemIndex={initialTopMostItemIndex}
        followOutput={followOutput}
        atBottomStateChange={onAtBottomStateChange}
        customScrollParent={parentScrollRef?.current}
        overscan={100}
        increaseViewportBy={VIRTUALIZED_LIST.VIEWPORT_INCREASE}
        components={{
          Header: components?.Header,
          Footer:
            components?.Footer ||
            (loading
              ? () => <DefaultLoader loading={loading} numItems={items?.length} />
              : undefined),
          EmptyPlaceholder: components?.EmptyPlaceholder,
        }}
      />
    </div>
  );
});

VirtualizedLogList.displayName = 'VirtualizedLogList';
